import type { OfficialFileMatch } from "./types";

interface ModrinthVersion {
  id: string;
  project_id: string;
  name: string;
  version_number: string;
  date_published: string;
  loaders: string[];
  game_versions: string[];
  files: Array<{
    hashes: { sha512?: string; sha1?: string };
    filename: string;
    primary: boolean;
  }>;
}

interface ModrinthProject {
  title?: string;
  slug?: string;
}

export function skippedOfficialLookup(hash: string): OfficialFileMatch {
  return {
    provider: "Modrinth",
    status: "skipped",
    checkedHash: hash,
    message:
      "Official-file lookup is disabled. The JAR itself remains entirely local.",
  };
}

export async function lookupOfficialModrinthFile(
  sha512: string,
  fileName: string,
  parentSignal?: AbortSignal,
): Promise<OfficialFileMatch> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), 6_000);
  const abort = () => controller.abort();
  parentSignal?.addEventListener("abort", abort, { once: true });
  try {
    const response = await fetch(
      `https://api.modrinth.com/v2/version_file/${sha512}?algorithm=sha512`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      },
    );
    if (response.status === 404) {
      return {
        provider: "Modrinth",
        status: "not-found",
        checkedHash: sha512,
        message:
          "No byte-for-byte Modrinth release matches this SHA-512. This is not a malware verdict.",
      };
    }
    if (!response.ok) {
      throw new Error(`Modrinth returned HTTP ${response.status}`);
    }
    const version = (await response.json()) as ModrinthVersion;
    const officialFile =
      version.files.find(
        (entry) => entry.hashes.sha512?.toLowerCase() === sha512.toLowerCase(),
      ) ??
      version.files.find((entry) => entry.primary) ??
      version.files[0];
    let project: ModrinthProject = {};
    try {
      const projectResponse = await fetch(
        `https://api.modrinth.com/v2/project/${version.project_id}`,
        {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        },
      );
      if (projectResponse.ok) {
        project = (await projectResponse.json()) as ModrinthProject;
      }
    } catch {
      // The exact version match remains valid without the display metadata.
    }
    return {
      provider: "Modrinth",
      status: "matched",
      checkedHash: sha512,
      projectId: version.project_id,
      projectTitle: project.title,
      projectSlug: project.slug,
      versionId: version.id,
      versionName: version.name,
      versionNumber: version.version_number,
      publishedAt: version.date_published,
      loaders: version.loaders,
      gameVersions: version.game_versions,
      officialFileName: officialFile?.filename,
      fileNameMatches: officialFile
        ? officialFile.filename.toLowerCase() === fileName.toLowerCase()
        : undefined,
      message:
        "The uploaded bytes exactly match a file published on Modrinth. This establishes release identity, not that every behavior is harmless.",
    };
  } catch (error) {
    if (parentSignal?.aborted) throw error;
    return {
      provider: "Modrinth",
      status: "unavailable",
      checkedHash: sha512,
      message:
        error instanceof DOMException && error.name === "AbortError"
          ? "The hash-only Modrinth lookup timed out."
          : "Modrinth could not be reached. Local analysis completed normally.",
    };
  } finally {
    globalThis.clearTimeout(timeout);
    parentSignal?.removeEventListener("abort", abort);
  }
}

